import axios from "axios";
import { isApiUnreachable } from "./apiAvailability";
import { API_ROUTE, Routes } from "./Routes";
import { writeLog } from "./ErrorLogger";
import { clearVaultKeys, getVmk } from "./vaultKeyStore";
import { clearNavigationStateCache } from "./navigationStateCache";

// ============================================================================
// TYPES
// ============================================================================

type HttpMethod = "get" | "post" | "put" | "delete" | "patch";

type RouteParams = Record<string, string | number>;
type QueryParams = Record<string, string | number | boolean | undefined>;

// ============================================================================
// STATE MANAGEMENT
// ============================================================================

let refreshPromise: Promise<string | null> | null = null;

const TOKEN_REFRESH_SKEW_SECONDS = 60;

const normalizeBase64 = (value: string): string =>
  value
    .replace(/\s+/g, "")
    .replace(/-/g, "+")
    .replace(/_/g, "/");

const decodeBase64Length = (value: string): number | null => {
  try {
    const normalized = normalizeBase64(value);
    const padded = normalized.padEnd(
      normalized.length + ((4 - (normalized.length % 4)) % 4),
      "="
    );

    return atob(padded).length;
  } catch {
    return null;
  }
};

const truncate = (value: string, maxLength = 1200): string =>
  value.length <= maxLength
    ? value
    : `${value.slice(0, maxLength)}...[truncated]`;

const safeJsonStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

const summarizeValidationErrors = (errors: unknown): string | null => {
  if (!errors || typeof errors !== "object" || Array.isArray(errors)) {
    return null;
  }

  const entries = Object.entries(errors as Record<string, unknown>)
    .map(([field, messages]) => {
      if (!Array.isArray(messages)) {
        return `${field}: invalid`;
      }

      const printable = messages
        .map((message) => (typeof message === "string" ? message : safeJsonStringify(message)))
        .join(", ");

      return `${field}: ${printable}`;
    });

  if (entries.length === 0) {
    return null;
  }

  return truncate(entries.join(" ; "), 900);
};

const summarizeResponseData = (data: unknown): string | null => {
  if (data === undefined || data === null) {
    return null;
  }

  if (typeof data === "string") {
    return truncate(data, 1200);
  }

  if (typeof data !== "object") {
    return truncate(String(data), 1200);
  }

  if (Array.isArray(data)) {
    return truncate(`array(length=${data.length})`, 1200);
  }

  const candidate = data as Record<string, unknown>;
  const parts: string[] = [];

  if (typeof candidate.title === "string") {
    parts.push(`title=${candidate.title}`);
  }

  if (typeof candidate.message === "string") {
    parts.push(`message=${candidate.message}`);
  }

  if (typeof candidate.detail === "string") {
    parts.push(`detail=${candidate.detail}`);
  }

  if (typeof candidate.traceId === "string") {
    parts.push(`traceId=${candidate.traceId}`);
  }

  if (typeof candidate.type === "string") {
    parts.push(`type=${candidate.type}`);
  }

  if (typeof candidate.status === "number") {
    parts.push(`status=${candidate.status}`);
  }

  const validationErrors = summarizeValidationErrors(candidate.errors);
  if (validationErrors) {
    parts.push(`errors=${validationErrors}`);
  }

  if (parts.length > 0) {
    return truncate(parts.join(" | "), 1200);
  }

  return truncate(safeJsonStringify(data), 1200);
};

const summarizePayload = (payload: unknown): string | null => {
  if (payload === undefined || payload === null) {
    return null;
  }

  if (Array.isArray(payload)) {
    return `array(length=${payload.length})`;
  }

  if (typeof payload !== "object") {
    return `type=${typeof payload}`;
  }

  const candidate = payload as Record<string, unknown>;
  const parts: string[] = [];
  const keys = Object.keys(candidate).sort();

  if (keys.length > 0) {
    parts.push(`keys=${keys.join(",")}`);
  }

  const emptyGuidFields = ["idOwner", "globalKeyUserId", "probateKeyUserId", "kGroupKeyUserId"]
    .filter((key) => candidate[key] === "");

  if (emptyGuidFields.length > 0) {
    parts.push(`emptyGuidFields=${emptyGuidFields.join(",")}`);
  }

  if (typeof candidate.title === "string") {
    parts.push(`titleLength=${candidate.title.length}`);
  }

  if (typeof candidate.note === "string") {
    parts.push(`noteBytes=${decodeBase64Length(candidate.note) ?? "unknown"}`);
  }

  if (Array.isArray(candidate.dataFields)) {
    const dataFields = candidate.dataFields as Array<Record<string, unknown>>;
    parts.push(`dataFields=${dataFields.length}`);

    const encryptedFields = dataFields.filter((field) =>
      Boolean(field && typeof field === "object" && typeof field.valueEncrypted === "string" && field.valueEncrypted.length > 0)
    ).length;

    const fileFields = dataFields.filter((field) =>
      Boolean(field && typeof field === "object" && typeof field.encryptedFileBytesBase64 === "string" && field.encryptedFileBytesBase64.length > 0)
    ).length;

    if (encryptedFields > 0) {
      parts.push(`encryptedFields=${encryptedFields}`);
    }

    if (fileFields > 0) {
      parts.push(`fileFields=${fileFields}`);
    }
  }

  return parts.length > 0
    ? truncate(parts.join(" | "), 1200)
    : truncate(safeJsonStringify(payload), 1200);
};

const getErrorMessage = (error: unknown): string =>
  error instanceof Error ? error.message : String(error);

export const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const responseData = error.response?.data;

    if (typeof responseData === "string" && responseData.trim()) {
      return responseData.trim();
    }

    if (responseData && typeof responseData === "object") {
      const candidate = responseData as Record<string, unknown>;
      for (const key of ["message", "detail"] as const) {
        const value = candidate[key];
        if (typeof value === "string" && value.trim()) {
          return value.trim();
        }
      }

      const validationErrors = summarizeValidationErrors(candidate.errors);
      if (validationErrors) {
        return validationErrors;
      }

      if (typeof candidate.title === "string" && candidate.title.trim()) {
        return candidate.title.trim();
      }
    }

    return fallback;
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};

const extractTraceId = (headers: unknown): string | undefined => {
  if (!headers || typeof headers !== "object") {
    return undefined;
  }

  const candidate = headers as Record<string, unknown> & {
    get?: (name: string) => unknown;
  };
  const traceId =
    candidate.get?.("x-trace-id") ??
    candidate.get?.("X-Trace-Id") ??
    candidate["x-trace-id"] ??
    candidate["X-Trace-Id"];

  return typeof traceId === "string" && traceId.trim() !== ""
    ? traceId
    : undefined;
};

const redirectToMaintenance = (): void => {
  if (typeof window === "undefined") {
    return;
  }

  if (window.location.pathname !== "/maintenance") {
    window.location.href = "/maintenance";
  }
};

// ============================================================================
// ROUTE HELPER UTILITIES (Internal)
// ============================================================================

/**
 * Replace route parameters with actual values
 * @internal
 * @example replaceParams('datacards/{cardId}', { cardId: '123' }) 
 * // Returns: 'datacards/123'
 */
const replaceParams = (route: string, params: RouteParams): string => {
  let result = route;
  Object.entries(params).forEach(([key, value]) => {
    result = result.replace(`{${key}}`, String(value));
  });
  return result;
};

/**
 * Build query string from object
 * @internal
 * @example buildQueryString({ page: 1, limit: 10 })
 * // Returns: '?page=1&limit=10'
 */
const buildQueryString = (params: QueryParams): string => {
  const filtered = Object.entries(params)
    .filter(([, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${key}=${encodeURIComponent(String(value))}`);

  return filtered.length > 0 ? `?${filtered.join("&")}` : "";
};

/**
 * Build complete endpoint with route and query parameters
 * @internal
 */
const buildEndpoint = (
  route: string,
  routeParams?: RouteParams,
  queryParams?: QueryParams
): string => {
  let endpoint = route;

  // Replace route parameters
  if (routeParams) {
    endpoint = replaceParams(route, routeParams);
  }

  // Add query parameters
  if (queryParams) {
    endpoint += buildQueryString(queryParams);
  }

  return endpoint;
};

// ============================================================================
// TOKEN MANAGEMENT
// ============================================================================

async function getValidAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }

  return refreshPromise;
}

export async function refreshAccessToken(): Promise<string | null> {
  try {
    const response = await axios.get(`${API_ROUTE}${Routes.REFRESH_TOKEN}`, {
      withCredentials: true,
    });

    if (response.status === 200) {
      const newToken = response.data.accessToken;
      sessionStorage.setItem("accessToken", newToken);
      sessionStorage.setItem("tokenUpdatedOn", new Date().toUTCString());
      extendCookieExpiry("fullName", 31);
      extendCookieExpiry("email", 31);
      return newToken;
    }
    throw new Error("Refresh token failed");
  } catch (err) {
    console.error("Refresh token failed:", err);
    if (isApiUnreachable(err)) {
      redirectToMaintenance();
    }
    return null;
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) {
      return null;
    }

    const normalizedPayload = payload.replace(/-/g, "+").replace(/_/g, "/");
    const paddedPayload = normalizedPayload.padEnd(
      normalizedPayload.length + ((4 - (normalizedPayload.length % 4)) % 4),
      "="
    );

    return JSON.parse(atob(paddedPayload));
  } catch {
    return null;
  }
}

export function getAccessTokenPayload(): Record<string, unknown> | null {
  const token = sessionStorage.getItem("accessToken");
  return token ? decodeJwtPayload(token) : null;
}

export function hasInternalAdminAccess(): boolean {
  const payload = getAccessTokenPayload();
  const adminLevelValue = payload?.["admin_level"] ?? payload?.["adminLevel"];

  const adminLevel =
    typeof adminLevelValue === "number"
      ? adminLevelValue
      : typeof adminLevelValue === "string"
        ? Number.parseInt(adminLevelValue, 10)
        : 0;

  return Number.isFinite(adminLevel) && adminLevel > 0;
}

function shouldRefreshAccessToken(token: string | null): boolean {
  if (!token) {
    return true;
  }

  const payload = decodeJwtPayload(token);
  const expiresAt = typeof payload?.exp === "number" ? payload.exp : null;

  if (!expiresAt) {
    return false;
  }

  const nowInSeconds = Math.floor(Date.now() / 1000);
  return expiresAt <= nowInSeconds + TOKEN_REFRESH_SKEW_SECONDS;
}

// ============================================================================
// CORE REQUEST FUNCTION
// ============================================================================

// eslint-disable-next-line @typescript-eslint/no-explicit-any
async function requestWithToken<T = any>(
  method: HttpMethod,
  endpoint: string,
  payload?: unknown,
  responseType: "json" | "blob" = "json"
): Promise<T> {
  checkSignIn();

  let token = sessionStorage.getItem("accessToken");

  if (shouldRefreshAccessToken(token)) {
    token = await getValidAccessToken();

    if (!token) {
      window.location.href = "/login";
      throw new Error("Unauthorized");
    }
  }

  try {
    const response = await axios({
      method,
      url: `${API_ROUTE}${endpoint}`,
      data: payload,
      withCredentials: true,
      headers: { Authorization: `Bearer ${token}` },
      responseType: responseType === "blob" ? "blob" : "json",
    });

    return response.data;
  } catch (error: unknown) {
    let failure = error;

    // Handle 401 Unauthorized - token expired
    if (axios.isAxiosError(failure) && failure.response?.status === 401) {
      const newToken = await getValidAccessToken();

      if (!newToken) {
        window.location.href = "/login";
        throw new Error("Unauthorized");
      }

      try {
        const retryResponse = await axios({
          method,
          url: `${API_ROUTE}${endpoint}`,
          data: payload,
          withCredentials: true,
          headers: { Authorization: `Bearer ${newToken}` },
          responseType: responseType === "blob" ? "blob" : "json",
        });

        return retryResponse.data;
      } catch (retryError: unknown) {
        failure = retryError;
      }
    }

    // Log error
    if (endpoint !== Routes.LOG_ERROR) {
      const statusCode = axios.isAxiosError(failure) ? failure.response?.status : undefined;
      const isNonOperationalFailure =
        typeof statusCode === "number" &&
        statusCode >= 400 &&
        statusCode < 500;

      writeLog(
        isNonOperationalFailure ? "WARN" : "API_ERR",
        `${method.toUpperCase()} ${endpoint} failed | ${getErrorMessage(failure)}`,
        {
          eventAction: isNonOperationalFailure ? "client.api_warning" : "client.api_error",
          statusCode,
          traceId: axios.isAxiosError(failure) ? extractTraceId(failure.response?.headers) : undefined,
          requestPath: endpoint,
          responseBody: axios.isAxiosError(failure) ? summarizeResponseData(failure.response?.data) ?? undefined : undefined,
          payloadSummary: summarizePayload(payload) ?? undefined,
          errorCode: axios.isAxiosError(failure) ? failure.code : undefined
        }
      );
    }

    if (isApiUnreachable(failure)) {
      redirectToMaintenance();
    }

    throw failure;
  }
}

// ============================================================================
// HTTP METHOD WRAPPERS WITH ROUTE BUILDING
// ============================================================================

/**
 * GET request with automatic route building
 * @param route - Route path from Routes object
 * @param routeParams - Parameters to replace in route (e.g., {cardId})
 * @param queryParams - Query string parameters (e.g., ?categoryId=123)
 * 
 * @example
 * // Simple GET
 * await get(Routes.GET_CATEGORIES);
 * 
 * @example
 * // GET with route params
 * await get(Routes.GET_DATACARD, { cardId: '123' });
 * // Calls: GET /api/v1/datacards/123
 * 
 * @example
 * // GET with query params (for filtering)
 * await get(Routes.GET_CARDTEMPLATE_By_Category, undefined, { categoryId: '456' });
 * // Calls: GET /api/v1/cardtemplates?categoryId=456
 * 
 * @example
 * // GET with both route and query params
 * await get(Routes.GET_ENCRYPTED_FILE, { cardId: '123' }, { ext: 'pdf' });
 * // Calls: GET /api/v1/files/123?ext=pdf
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const get = <T = any>(
  route: string,
  routeParams?: RouteParams,
  queryParams?: QueryParams,
  responseType: "json" | "blob" = "json"
): Promise<T> => {
  const endpoint = buildEndpoint(route, routeParams, queryParams);
  return requestWithToken<T>("get", endpoint, undefined, responseType);
};

/**
 * POST request with automatic route building
 * @param route - Route path from Routes object
 * @param payload - Request body data
 * @param routeParams - Parameters to replace in route (optional)
 * @param queryParams - Query string parameters (optional)
 * 
 * @example
 * // Create data card
 * await post(Routes.SET_FIELDS_VALUE, { title: 'My Card', dataFields: [...] });
 * 
 * @example
 * // Create field template
 * await post(Routes.CREATE_FIELD, { label: 'Email', dataType: 'text' });
 * 
 * @example
 * // Add missing keys
 * await post(Routes.ADD_MISSING_KEYS, { emailId: 'user@example.com', keys: [...] });
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const post = <T = any>(
  route: string,
  payload: unknown,
  routeParams?: RouteParams,
  queryParams?: QueryParams
): Promise<T> => {
  const endpoint = buildEndpoint(route, routeParams, queryParams);
  return requestWithToken<T>("post", endpoint, payload);
};

/**
 * PUT request with automatic route building
 * @param route - Route path from Routes object
 * @param routeParams - Parameters to replace in route (e.g., {cardId})
 * @param payload - Request body data
 * @param queryParams - Query string parameters (optional)
 * 
 * @example
 * // Update data card
 * await put(Routes.UPDATE_FIELDS_VALUE, { cardId: '123' }, { title: 'Updated' });
 * // Calls: PUT /api/v1/datacards/123
 * 
 * @example
 * // Update card template
 * await put(Routes.UPDATE_DATACARD, { cardTemplateId: '456' }, { name: 'New Name' });
 * // Calls: PUT /api/v1/cardtemplates/456
 * 
 * @example
 * // Link to parent card
 * await put(Routes.LINK_TO_PARENT_CARD, { cardId: '789' }, { idDataCard: '789', ownerId: '111' });
 * // Calls: PUT /api/v1/datacards/789/parent
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const put = <T = any>(
  route: string,
  routeParams: RouteParams,
  payload: unknown,
  queryParams?: QueryParams
): Promise<T> => {
  const endpoint = buildEndpoint(route, routeParams, queryParams);
  return requestWithToken<T>("put", endpoint, payload);
};

/**
 * PATCH request with automatic route building
 * @param route - Route path from Routes object
 * @param routeParams - Parameters to replace in route (e.g., {cardTemplateId})
 * @param payload - Request body data
 * @param queryParams - Query string parameters (optional)
 * 
 * @example
 * // Save probate notes
 * await patch(Routes.SAVE_PROBATE_NOTES, { cardTemplateId: '456' }, { notes: 'text', status: 1 });
 * // Calls: PATCH /api/v1/probates/logs/456
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const patch = <T = any>(
  route: string,
  routeParams: RouteParams,
  payload: unknown,
  queryParams?: QueryParams
): Promise<T> => {
  const endpoint = buildEndpoint(route, routeParams, queryParams);
  return requestWithToken<T>("patch", endpoint, payload);
};

/**
 * DELETE request with automatic route building
 * @param route - Route path from Routes object
 * @param routeParams - Parameters to replace in route (e.g., {userId})
 * @param payload - Request body data (optional)
 * @param queryParams - Query string parameters (optional)
 * 
 * @example
 * // Delete user
 * await del(Routes.DELETE_USER, { userId: '789' }, { userId: '789', email: 'user@example.com' });
 * // Calls: DELETE /api/v1/users/789
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const del = <T = any>(
  route: string,
  routeParams: RouteParams,
  payload?: unknown,
  queryParams?: QueryParams
): Promise<T> => {
  const endpoint = buildEndpoint(route, routeParams, queryParams);
  return requestWithToken<T>("delete", endpoint, payload);
};

// ============================================================================
// LEGACY ALIASES (for backward compatibility - can remove after migration)
// ============================================================================

/** @deprecated Use `get` instead */
export const getWithToken = get;

/** @deprecated Use `post` instead */
export const postWithToken = post;

/** @deprecated Use `put` instead */
export const putWithToken = put;

/** @deprecated Use `patch` instead */
export const patchWithToken = patch;

/** @deprecated Use `del` instead */
export const deleteWithToken = del;

/** @deprecated Use `post` instead */
export const TokenWithPostCall = post;

/** @deprecated Use `put` instead */
export const TokenWithPutCall = put;

/** @deprecated Use `del` instead */
export const TokenWithDeleteCall = del;

// ============================================================================
// AUTHENTICATION & SESSION MANAGEMENT
// ============================================================================

/**
 * Validate email format
 */
export default function validateEmail(email: string): string | null {
  if (!email.trim()) return "Email is required";

  const trimmedEmail = email.trim();
  const atIndex = trimmedEmail.indexOf("@");
  const hasSingleAt = atIndex > 0 && atIndex === trimmedEmail.lastIndexOf("@") && atIndex < trimmedEmail.length - 1;

  if (!hasSingleAt) {
    return "Please enter a valid email address";
  }

  const localPart = trimmedEmail.slice(0, atIndex);
  const domainPart = trimmedEmail.slice(atIndex + 1);
  const localPartPattern = /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~.-]+$/;

  if (
    localPart.length > 64 ||
    localPart.startsWith(".") ||
    localPart.endsWith(".") ||
    localPart.includes("..") ||
    !localPartPattern.test(localPart)
  ) {
    return "Please enter a valid email address";
  }

  if (
    domainPart.length > 255 ||
    domainPart.startsWith(".") ||
    domainPart.endsWith(".") ||
    domainPart.includes("..")
  ) {
    return "Please enter a valid email address";
  }

  const labels = domainPart.split(".");
  if (
    labels.length < 2 ||
    labels.some((label) =>
      !label ||
      label.length > 63 ||
      label.startsWith("-") ||
      label.endsWith("-") ||
      !/^[A-Za-z0-9-]+$/.test(label)
    )
  ) {
    return "Please enter a valid email address";
  }

  return "";
}

/**
 * Sign out user and clear all session data
 */
export async function signOut(): Promise<void> {
  try {
    await axios.post(
      `${API_ROUTE}${Routes.LOGOUT}`,
      {},
      { withCredentials: true }
    );
  } catch (err) {
    console.warn("Logout request failed or skipped:", err);
  }
  clearBrandingStyles();
  
  // Clear all session data
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("familyBranding");
  clearVaultKeys();
  sessionStorage.removeItem("tokenUpdatedOn");
  clearNavigationStateCache();
  deleteCookie("fullName");
  deleteCookie("email");
  localStorage.removeItem("salt");
  localStorage.removeItem("evmk");
  localStorage.removeItem("family");
  localStorage.removeItem("lastActivity");
  sessionStorage.removeItem("activeFamilyId");
}

export function hasStoredIdentity(): boolean {
  return (localStorage.getItem("salt") || "").trim() !== "";
}

export function hasUnlockedVault(): boolean {
  const vmkBase64 = getVmk() || "";
  const vmkLength = decodeBase64Length(vmkBase64);

  return vmkLength === 16 || vmkLength === 32;
}

export const clearBrandingStyles = () => {
  const root = document.documentElement;

  const vars = [
    '--brand-font-colour',
    '--brand-top-menu-bg',
    '--brand-top-menu-font',
    '--brand-page-bg',
    '--brand-page-box-border',
    '--brand-page-box-fill',
    '--brand-page-header-font',
    '--brand-page-strap-font',
    '--brand-folder-svg',
    '--brand-footer-bg',
    '--brand-footer-graphic',
  ];

  vars.forEach(variable => root.style.removeProperty(variable));
};

/**
 * Check if user is signed in, redirect to login if not.
 * If the user is still identified but the vault key was lost (e.g. a page
 * refresh, since it is deliberately kept in memory only), send them to the
 * lightweight unlock screen instead of forcing a full sign-out.
 */
export function checkSignIn(): void {
  if (!hasStoredIdentity()) {
    void signOut();
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!hasUnlockedVault()) {
    window.location.href = "/unlock";
    throw new Error("Unauthorized");
  }
}

// ============================================================================
// COOKIE UTILITIES
// ============================================================================

/**
 * Set a cookie with expiration
 */
export const setCookie = (
  name: string,
  value: string,
  minutes: number,
  path: string = "/"
): void => {
  const expires = new Date(Date.now() + minutes * 60 * 1000).toUTCString();
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=${path}; SameSite=Lax${secure}`;
};

/**
 * Get cookie value by name
 */
export const getCookie = (name: string): string | null => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? decodeURIComponent(match[2]) : null;
};

/**
 * Delete a cookie
 */
export const deleteCookie = (name: string, path: string = "/"): void => {
  const secure = typeof window !== "undefined" && window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; SameSite=Lax${secure}`;
};

/**
 * Extend cookie expiration time
 */
export const extendCookieExpiry = (
  name: string,
  minutes: number,
  path: string = "/"
): boolean => {
  const value = getCookie(name);
  if (!value) return false;

  setCookie(name, value, minutes, path);
  return true;
};
